import { z } from 'zod';

import { WindowKeySchema, DiversificationKindSchema, DIVERSIFICATION_KINDS } from './enums';
import { TickerSchema } from './primitives';

/** Pearson correlation is confined to [-1, 1]; enforce the domain at the boundary. */
export const RhoSchema = z.number().min(-1).max(1);

/** Defensive ceiling: product matrix is 10×10 sector or small peer sets. */
export const MATRIX_MAX = 64;

/** Peer/diversifier group size (plan §6.6 `:ticker/diversifiers`). */
export const DIVERSIFICATION_MEMBERS_MAX = 32;

export const CorrelationPairSchema = z.object({
  a: TickerSchema,
  b: TickerSchema,
  rho: RhoSchema,
  window: WindowKeySchema,
});
export type CorrelationPair = z.infer<typeof CorrelationPairSchema>;

export const CorrelationMatrixSchema = z
  .object({
    window: WindowKeySchema,
    tickers: z.array(TickerSchema).min(1).max(MATRIX_MAX),
    /** Square row-major matrix aligned with `tickers`; diagonal is 1. */
    values: z.array(z.array(RhoSchema).min(1).max(MATRIX_MAX)).min(1).max(MATRIX_MAX),
  })
  .superRefine((val, ctx) => {
    const n = val.tickers.length;
    if (val.values.length !== n) {
      ctx.addIssue({
        code: 'custom',
        path: ['values'],
        message: `matrix must be square: expected ${String(n)} rows, got ${String(val.values.length)}`,
      });
      return;
    }
    for (let i = 0; i < n; i++) {
      const row = val.values[i];
      if (row?.length !== n) {
        ctx.addIssue({
          code: 'custom',
          path: ['values', i],
          message: `matrix row ${String(i)} must have length ${String(n)}`,
        });
      }
    }
  });
export type CorrelationMatrix = z.infer<typeof CorrelationMatrixSchema>;

export const DiversificationMemberSchema = z.object({
  ticker: TickerSchema,
  rho: RhoSchema,
});
export type DiversificationMember = z.infer<typeof DiversificationMemberSchema>;

export const DiversificationGroupSchema = z.object({
  kind: DiversificationKindSchema,
  members: z.array(DiversificationMemberSchema).max(DIVERSIFICATION_MEMBERS_MAX),
});
export type DiversificationGroup = z.infer<typeof DiversificationGroupSchema>;

/** Most/least-correlated shortlists shown beside the groups (F15). */
export const CORRELATION_EXTREMES_MAX = 10;

/**
 * `GET /v1/securities/:ticker/diversifiers` (plan §6.6) — three mutually
 * exclusive groups PLUS the correlation extremes, in one payload because the
 * stock screen renders them together and both use the same pairwise correlation
 * definition (local mode shares one ranked scan for both).
 *
 * `.length(DIVERSIFICATION_KINDS.length)` plus kind membership and uniqueness
 * (order unconstrained on the wire) is deliberate: a group that finds no
 * qualifying candidate ships with `members: []` and its heading intact, so the
 * screen's panel set and keyboard order stay stable (plan §6.5a). Dropping the
 * group instead would make an empty result indistinguishable from a missing
 * rule. Evaluation order of the used-set is product-critical inside core only.
 */
export const DiversificationPayloadSchema = z
  .object({
    ticker: TickerSchema,
    window: WindowKeySchema,
    groups: z.array(DiversificationGroupSchema).length(DIVERSIFICATION_KINDS.length),
    most: z.array(DiversificationMemberSchema).max(CORRELATION_EXTREMES_MAX),
    least: z.array(DiversificationMemberSchema).max(CORRELATION_EXTREMES_MAX),
  })
  .superRefine((val, ctx) => {
    /* Evaluation order (used-set) is product-critical inside core; the wire
       only requires each kind exactly once so panels cannot drop or duplicate. */
    const kinds = val.groups.map((g) => g.kind);
    const expected = new Set<string>(DIVERSIFICATION_KINDS);
    const seen = new Set<string>();
    for (const kind of kinds) {
      if (!expected.has(kind) || seen.has(kind)) {
        ctx.addIssue({
          code: 'custom',
          path: ['groups'],
          message: `groups must contain each of ${DIVERSIFICATION_KINDS.join(', ')} exactly once`,
        });
        return;
      }
      seen.add(kind);
    }
  });
export type DiversificationPayload = z.infer<typeof DiversificationPayloadSchema>;
